import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import { removeRequest } from 'Flux/FluxUtils.ts'
import type { ChatStep } from '../../../Generated/ChatStep.ts'
import type { ChartUpdate } from '../../../Generated/ChartUpdate.ts'
import type { ComparisonCaveat } from '../../../Generated/ComparisonCaveat.ts'
import type { EvidenceRef } from '../../../Generated/EvidenceRef.ts'
import type { PinRefusal } from '../../../Generated/PinRefusal.ts'
import type { RenderedImage } from '../../../Generated/RenderedImage.ts'
import type { RenderedReport } from '../../../Generated/RenderedReport.ts'
import type { ReportBlock } from '../../../Generated/ReportBlock.ts'
import type { ReportEdit } from '../../../Generated/ReportEdit.ts'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import type { VizProvenance } from '../../../Generated/VizProvenance.ts'
import { applyChartUpdate, directiveKey, matchFocusRef } from './chatViz.js'
import type { SourceSharedState } from '../Visualization/VisualizationSliceTypes.js'
import type { ChatNode, NodeMap } from './conversationTree.js'
import { childrenOf, pathToRoot } from './conversationTree.js'

export interface IChatMessage {
  role: 'user' | 'assistant'
  content: string
  /** Tools the model called to produce this answer (assistant turns only). */
  steps?: ChatStep[]
  /** Charts the model asked us to render for this answer (assistant turns only). */
  directives?: VizDirective[]
  /** Deterministic "Evidence used" refs the agent looked at (Plan A — Epic #7; assistant turns only). */
  evidence?: EvidenceRef[]
  /** Server-rendered chart PNGs the agent produced for THIS turn, shown inline in the bubble (assistant turns only). */
  renderedImages?: RenderedImage[]
  /** Server-rendered print-styled report documents for THIS turn, shown inline + Save-as-PDF (assistant turns only). */
  renderedReports?: RenderedReport[]
  /** Workspace items the agent couldn't edit this turn because they're PINNED — shown as an inline notice + Unpin (U2b). */
  pinRefusals?: PinRefusal[]
  isError?: boolean
  /** True while this assistant turn is still being streamed (partial content / steps arriving live). */
  streaming?: boolean
  /** True when the user aborted this turn via Stop — the (possibly partial) answer is kept, labelled interrupted. */
  stopped?: boolean
}

/**
 * The persistent, agent-authored report document (IR-1). Blocks are edited by directive (`applyReportEdits`)
 * and rendered server-side to a self-contained `html` document shown in the ReportPanel. Mirrors the
 * `openGraphs` graph-canvas pattern: state lives here, one render call resolves per turn.
 */
export interface ICurrentReport {
  /** The report's ordered blocks. Every block carries a stable `id` (assigned on ADD/REPLACE if missing). */
  blocks: ReportBlock[]
  title?: string
  /** The last server-rendered document (`ReportRenderResponse.html`), or null before the first render. */
  html: string | null
  status: 'idle' | 'rendering' | 'error'
}

/** The canvas payload a turn resolves to (opened charts, an in-place update, a chart to focus). */
interface CanvasPayload {
  directives?: VizDirective[]
  focusChartRef?: string
  chartUpdate?: ChartUpdate
  /** Server-computed comparison confidence+caveat for a rendered comparison this turn. */
  comparisonCaveat?: ComparisonCaveat
  /**
   * The "current lens" the most recent query resolved to (blast source + SPIN/group knobs) — used to keep the
   * always-on composer indicator in sync. Optional per turn; absence means "unchanged" (keep the prior value).
   */
  resolvedContext?: VizProvenance
}

export interface IChatState {
  /**
   * The active-path CACHE (W-1): the linear root→`currentNodeId` messages the UI renders and the epic sends as
   * `history`. NOT a second source of truth — the {@link conversationNodes} TREE is authoritative; every
   * mutating reducer rebuilds this via `syncActivePath`. Kept as a stored field (not a pure selector) so the
   * many `state.chat.messages` readers and the streaming reducers stay unchanged and low-risk.
   */
  messages: IChatMessage[]
  /**
   * The conversation TREE (W-1) — the source of truth. Linear-by-default: with no branches it is a single
   * chain and `messages` looks exactly like before. Branch an aside from any node, explore, return; the main
   * line stays clean. Persisted with a saved workspace (v2 blob).
   */
  conversationNodes: NodeMap
  /** The node the next turn extends / the streaming assistant turn lands on. Null = empty conversation. */
  currentNodeId: string | null
  /** The tree's single root (first user turn), or null when empty. */
  rootNodeId: string | null
  openRequests: string[]
  /** `${scopeType}:${scopeId}` of the conversation, so we can reset when the user switches scope. */
  scopeKey?: string
  /**
   * A stable, client-generated id for THIS chat session (a UUID), sent as `ChatRequest.conversationId` on every
   * turn. It keys the backend's SOFT, conversation-scoped tool-call cache (partitioned server-side by the
   * authenticated user, so a colliding id can never cross users). Minted once when the conversation starts and
   * re-minted on reset / workspace resume, so a fresh session never reads a prior session's cached tool results.
   */
  conversationId: string
  /** Live Krona zoom, keyed by contextId — reported to the model as part of the on-screen viz state. */
  kronaSelections: Record<string, string[]>
  /** Charts currently on the canvas beside the chat (deduped by directive identity), in open order. */
  openGraphs: VizDirective[]
  /** directiveKey of the graph to highlight/scroll-to (newly opened, or pointed at via focusChart). */
  focusedGraphKey?: string
  /** Server-computed comparison caveat for the charts currently on the canvas (banner above the panels). */
  comparisonCaveat?: ComparisonCaveat
  /**
   * "Cite sources" toggle (Epic A #7 Tier 2). When ON, the SendMessageEpic sends `citeSources: true` so the
   * model appends inline `[eN]` markers; when OFF the field is omitted and the backend uses its provider
   * default (Claude cites richly, Qwen leanly). A user preference — persists across scope switches.
   */
  citeSources: boolean
  /**
   * The user's question from the most recently FAILED turn (503/transport/`failStream`). Retained so the
   * composer can restore it as a fallback (item #9) — the primary affordance is the per-bubble Retry button.
   * Cleared on the next send and on scope reset.
   */
  lastFailedQuestion?: string
  /**
   * "Ask about this" seed (fast-follow): a starter question the composer should pre-fill when a chart's
   * "Ask about this" affordance is clicked. The composer effect reads it and sets the draft (it does NOT
   * auto-send). Paired with `composerSeedNonce` so clicking the same chart twice re-fires even when the
   * generated text is identical (a bare string wouldn't change → the effect wouldn't run).
   */
  composerSeed?: string
  composerSeedNonce: number
  /**
   * The "current lens" — the blast source + knobs the MOST RECENT query resolved to (last-wins). Drives the
   * always-on "Current: …" indicator near the composer. Updated whenever a turn reports a non-null
   * `resolvedContext`; a lens-less turn KEEPS the prior value (never blanks the indicator). Cleared on scope reset.
   */
  currentLens?: VizProvenance
  /** The agent-authored report document (IR-1) — blocks + the last server render. Cleared on scope reset. */
  currentReport: ICurrentReport
  /**
   * Monotonic counter bumped whenever the report changes (`applyReportEdits`). Drives the ReportPanel tab's
   * "unread" dot: the page compares it to the revision last seen while the Report tab was active.
   */
  reportRevision: number
  /**
   * Workspace-global pinned facts/prose that bridge conversation branches (Workspace epic, W-1). Reserved in
   * W-0 — persisted with a saved workspace and rehydrated on resume, but not yet populated by any UI. Cleared
   * on scope reset.
   */
  globalContext: string[]
  /**
   * W-2 unified Workspace panel. The panel derives its item list from existing state (`openGraphs` → chart
   * items, `currentReport` with blocks → one report item) PLUS these two fields:
   *  - {@link notes}: user-authored NOTE items (their own card meta lives on the note);
   *  - {@link pinnedItemIds}: which workspace-item ids the user pinned (chart:`<directiveKey>` / `report` /
   *    note:`<noteId>`). Pinned = user-committed, surfaced at the top of the panel.
   * Both persist with a saved workspace (v3). `itemMeta` holds the editable title/subtitle OVERRIDES for the
   * derived chart/report items (notes carry their own). All three cleared on scope reset.
   */
  notes: WorkspaceNote[]
  pinnedItemIds: string[]
  itemMeta: Record<string, WorkspaceItemMeta>
}

/**
 * A manual NOTE item in the unified Workspace panel (W-2). Notes are free text the user authors alongside the
 * agent's charts + report; they carry their own editable `title`/`subtitle` (the card meta lives ON the note,
 * unlike chart/report cards whose meta lives in {@link IChatState.itemMeta}). Persisted with the workspace (v3).
 */
export interface WorkspaceNote {
  id: string
  title?: string
  subtitle?: string
  text: string
}

/** Per-item editable card meta (W-2) for the DERIVED chart/report items — keyed by workspace-item id. */
export interface WorkspaceItemMeta {
  title?: string
  subtitle?: string
  /** Free-text per-item note/context the user attaches to a chart/report card (W-3: "why this view matters").
   *  Persisted with the workspace (rides in itemMeta) and shown on the card + in exports. Notes-KIND items keep
   *  their body text instead and don't use this. */
  note?: string
  /** The chart's PINNED view (a full shared-state snapshot captured when the user pinned it). Persisted here —
   *  the VisualizationSlice source that actually renders it is in-session only — so on reload the chart comes
   *  back at the pinned view and "restore to pinned" still works. Chart items only; cleared on unpin. */
  pinnedView?: SourceSharedState
  /** The conversation node (assistant turn) whose directives created this chart — a persisted backlink so the
   *  card can jump to the turn that produced it (see setCurrentNode). Set in finishStream; chart items only. */
  sourceNodeId?: string
}

/** The empty report document (no blocks, nothing rendered yet). */
const emptyReport = (): ICurrentReport => ({ blocks: [], title: undefined, html: null, status: 'idle' })

/**
 * Monotonic block-id source. Blocks the agent authors may lack an `id`; we assign one on ADD/REPLACE so later
 * UPDATE/REMOVE/REORDER edits can target them. A simple sequence keeps ids stable + human-readable in devtools.
 */
let blockIdSeq = 0
function withId(block: ReportBlock): ReportBlock {
  if (block.id) return block
  blockIdSeq += 1
  return { ...block, id: `blk-${blockIdSeq}` }
}

const initialState: IChatState = {
  messages: [],
  conversationNodes: {},
  currentNodeId: null,
  rootNodeId: null,
  openRequests: [],
  scopeKey: undefined,
  conversationId: uuidv4(),
  kronaSelections: {},
  openGraphs: [],
  focusedGraphKey: undefined,
  comparisonCaveat: undefined,
  citeSources: false,
  lastFailedQuestion: undefined,
  composerSeed: undefined,
  composerSeedNonce: 0,
  currentLens: undefined,
  currentReport: emptyReport(),
  reportRevision: 0,
  globalContext: [],
  notes: [],
  pinnedItemIds: [],
  itemMeta: {}
}

/**
 * Rebuild the derived `messages` cache from the TREE (root→`currentNodeId`). MUST be called at the end of
 * every reducer that mutates the tree, `currentNodeId`, or a node's message — Immer does not reflect a node
 * mutation into a separately-held `messages` array, so we re-map the (same) message references here. The cache
 * therefore always equals the active path and existing `state.chat.messages` readers keep working unchanged.
 */
function syncActivePath(state: IChatState): void {
  state.messages = pathToRoot(state.conversationNodes as NodeMap, state.currentNodeId).map((n) => n.message)
}

/** The node the conversation currently sits on (the streaming assistant turn during a live turn), if any. */
function currentNode(state: IChatState): ChatNode | undefined {
  return state.currentNodeId ? (state.conversationNodes[state.currentNodeId] as ChatNode | undefined) : undefined
}

/**
 * The message a stream action must write to. With an explicit turn [nodeId] (the epic threads one per turn) it
 * targets THAT node and nothing else — a late event whose node was reset/dropped is discarded, NEVER redirected
 * onto whatever turn happens to be current now. Without turn identity a delta arriving after a scope switch or a
 * mid-stream branch switch lands on the new turn's placeholder (cross-talk / corruption). Without an id (legacy
 * callers / unit tests) it falls back to the current node.
 */
function targetMessage(state: IChatState, nodeId: string | undefined): IChatMessage | undefined {
  return nodeId !== undefined
    ? (state.conversationNodes[nodeId] as ChatNode | undefined)?.message
    : currentNode(state)?.message
}

/**
 * Append `message` as a child of `currentNodeId` and advance `currentNodeId` to it (a new user turn, or the
 * streaming assistant placeholder). The first node ever appended becomes the root. Callers must
 * `syncActivePath` afterwards (batched so a two-node append rebuilds the cache once).
 */
function appendNode(state: IChatState, message: IChatMessage): ChatNode {
  const node: ChatNode = { id: uuidv4(), parentId: state.currentNodeId, message }
  state.conversationNodes[node.id] = node
  if (!state.rootNodeId) state.rootNodeId = node.id
  state.currentNodeId = node.id
  return node
}

/** Delete a node and its whole subtree (used by Retry's `dropTurn`). Depth-first, so no orphans are left. */
function removeSubtree(state: IChatState, nodeId: string): void {
  const stack = [nodeId]
  while (stack.length > 0) {
    const id = stack.pop() as string
    childrenOf(state.conversationNodes as NodeMap, id).forEach((k) => stack.push(k.id))
    delete state.conversationNodes[id]
  }
}

/** The user's most recent question (used to preserve it for Retry when a turn fails). */
function lastUserQuestion(state: IChatState): string | undefined {
  for (let i = state.messages.length - 1; i >= 0; i -= 1) {
    if (state.messages[i].role === 'user') return state.messages[i].content
  }
  return undefined
}

/** The action type pushed into `openRequests` per in-flight turn (mirrors `chat/sendMessageRequest`). */
const SEND_REQUEST_TYPE = 'chat/sendMessageRequest'

/** Drop a single in-flight `sendMessageRequest` marker (concurrent turns each push/pop exactly one). */
function popOpenRequest(state: IChatState): void {
  const idx = state.openRequests.indexOf(SEND_REQUEST_TYPE)
  if (idx >= 0) state.openRequests.splice(idx, 1)
}

/**
 * Apply a turn's canvas payload to the graph panel: upsert opened/changed charts (dedup by identity, keep
 * open order), apply an in-place `updateChart`, and resolve which chart to focus. Applied by the streaming
 * `finishStream` when a turn resolves.
 */
function applyCanvasPayload(state: IChatState, payload: CanvasPayload): void {
  // Capture the "current lens" (last-wins). A turn that doesn't report a resolvedContext leaves the prior lens
  // in place — a lens-less turn (e.g. pure prose) must NOT blank the always-on composer indicator.
  if (payload.resolvedContext) state.currentLens = payload.resolvedContext

  const directives = payload.directives ?? []
  directives.forEach((d) => {
    const key = directiveKey(d)
    const idx = state.openGraphs.findIndex((g) => directiveKey(g) === key)
    if (idx >= 0) state.openGraphs[idx] = d
    else state.openGraphs.push(d)
  })

  // A turn that renders charts refreshes the banner to match what it just rendered (set when the turn bound
  // a caveat, cleared when it didn't); a pure-prose turn (no new directives) leaves the existing banner.
  if (directives.length > 0) state.comparisonCaveat = payload.comparisonCaveat

  const update = payload.chartUpdate
  if (update) {
    const targetKey = matchFocusRef(state.openGraphs, update.ref)
    const idx = targetKey ? state.openGraphs.findIndex((g) => directiveKey(g) === targetKey) : -1
    if (idx >= 0) {
      const merged = applyChartUpdate(state.openGraphs[idx], update)
      const mergedKey = directiveKey(merged)
      state.openGraphs[idx] = merged
      // If the change made it identical to another open chart, drop that duplicate.
      state.openGraphs = state.openGraphs.filter((g, i) => i === idx || directiveKey(g) !== mergedKey)
      state.focusedGraphKey = mergedKey
    }
  }

  // Focus: an explicit focusChart wins; otherwise draw attention to the last chart opened this turn.
  const ref = payload.focusChartRef
  const resolved = ref ? matchFocusRef(state.openGraphs, ref) : undefined
  if (resolved) state.focusedGraphKey = resolved
  else if (!update && directives.length > 0) {
    state.focusedGraphKey = directiveKey(directives[directives.length - 1])
  }
}

/** 503-aware assistant error text (shared by the streaming and non-streaming failure paths). */
function errorText(status: number | undefined): string {
  return status === 503
    ? 'The AI assistant is temporarily unavailable. Please try again in a moment.'
    : 'Sorry — I could not answer that just now. Please try again.'
}

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    sendMessageRequest: (state, action: PayloadAction<{ scopeType: string; scopeId: string; message: string }>) => {
      // A fresh send supersedes any retained failed question (the composer fallback in item #9).
      state.lastFailedQuestion = undefined
      // W-1: append the user turn, then the streaming assistant placeholder, as a child chain under the current
      // node. `currentNodeId` now points at the streaming assistant node the stream actions fill in. With no
      // prior branching this is a plain linear extension.
      appendNode(state, { role: 'user', content: action.payload.message })
      appendNode(state, { role: 'assistant', content: '', steps: [], streaming: true })
      state.openRequests.push(action.type)
      syncActivePath(state)
    },
    sendMessageFailure: (state, action: PayloadAction<Error>) => {
      state.openRequests = removeRequest(state, action)
      // 503 = the backend caught the AI/agent loop being unavailable (e.g. the LLM server down) — give a
      // clearer, reassuring message than the generic failure text.
      const status = (action.payload as { status?: number }).status
      // Preserve the user's question so it isn't lost (Retry button + composer fallback, item #9).
      state.lastFailedQuestion = lastUserQuestion(state)
      appendNode(state, { role: 'assistant', content: errorText(status), isError: true })
      syncActivePath(state)
    },
    /** A tool call finished — append it to the streaming assistant turn's step trace (live progress). */
    addStreamStep: (state, action: PayloadAction<{ nodeId?: string; step: ChatStep }>) => {
      const msg = targetMessage(state, action.payload.nodeId)
      if (!msg) return
      if (!msg.steps) msg.steps = []
      msg.steps.push(action.payload.step)
      syncActivePath(state)
    },
    /** A chunk of the answer arrived — append it to the streaming assistant turn's content. */
    appendAnswerDelta: (state, action: PayloadAction<{ nodeId?: string; text: string }>) => {
      const msg = targetMessage(state, action.payload.nodeId)
      if (!msg) return
      msg.content += action.payload.text
      syncActivePath(state)
    },
    /**
     * The turn resolved: attach its charts + evidence, apply the canvas payload, and clear the streaming flag.
     * Evidence and rendered chart images are PER-MESSAGE (unlike the canvas-level comparison caveat), so they
     * land on the message, not the canvas — hence the extra `evidence?`/`renderedImages?`/`renderedReports?` on
     * the payload alongside the shared [CanvasPayload] fields.
     */
    finishStream: (
      state,
      action: PayloadAction<
        CanvasPayload & {
          nodeId?: string
          evidence?: EvidenceRef[]
          renderedImages?: RenderedImage[]
          renderedReports?: RenderedReport[]
          pinRefusals?: PinRefusal[]
        }
      >
    ) => {
      popOpenRequest(state)
      const msg = targetMessage(state, action.payload.nodeId)
      if (msg) {
        msg.directives = action.payload.directives
        msg.evidence = action.payload.evidence
        msg.renderedImages = action.payload.renderedImages
        msg.renderedReports = action.payload.renderedReports
        msg.pinRefusals = action.payload.pinRefusals
        msg.streaming = false
      }
      applyCanvasPayload(state, action.payload)
      // Provenance backlink (W-3): remember which turn (assistant node) produced each chart, so the workspace
      // card can jump back to it — this turn's own node, not wherever the active path sits now.
      const turnNodeId = action.payload.nodeId ?? state.currentNodeId
      if (turnNodeId) {
        for (const d of action.payload.directives ?? []) {
          const id = `chart:${directiveKey(d)}`
          const meta = state.itemMeta[id] ?? {}
          meta.sourceNodeId = turnNodeId
          state.itemMeta[id] = meta
        }
      }
      syncActivePath(state)
    },
    /** The turn failed (in-band engine `error`, or an HTTP/transport error) — surface the error text. */
    failStream: (state, action: PayloadAction<{ nodeId?: string; status: number | undefined }>) => {
      popOpenRequest(state)
      // Preserve the user's question so it isn't lost (Retry button + composer fallback, item #9).
      state.lastFailedQuestion = lastUserQuestion(state)
      const msg = targetMessage(state, action.payload.nodeId)
      if (msg && msg.role === 'assistant') {
        msg.content = errorText(action.payload.status)
        msg.isError = true
        msg.streaming = false
      } else {
        appendNode(state, { role: 'assistant', content: errorText(action.payload.status), isError: true })
      }
      syncActivePath(state)
    },
    /**
     * The user aborted an in-flight turn via Stop (item #8). The SendMessageEpic's `takeUntil(cancelStream)`
     * tears down the SSE subscription (→ `controller.abort()`); here we close the streaming placeholder
     * cleanly: drop the streaming flag (no dangling "streaming" state), mark it `stopped` (NOT an error), and
     * keep whatever partial answer had streamed in — labelled interrupted in the UI.
     */
    cancelStream: (state) => {
      popOpenRequest(state)
      // Target the node that is actually STREAMING, not `currentNode`: the user may have branch-switched
      // mid-stream (moving currentNodeId off the live turn), and closing the current node would freeze the wrong
      // bubble while the real streaming placeholder spins forever. Exactly one turn streams at a time, so this is
      // unambiguous — the same turn-identity discipline the stream reducers use via `targetMessage`.
      const streamingNode = Object.values(state.conversationNodes).find(
        (n) => (n as ChatNode).message.streaming,
      ) as ChatNode | undefined
      const msg = streamingNode?.message
      if (msg) {
        msg.streaming = false
        msg.stopped = true
      }
      syncActivePath(state)
    },
    /**
     * Drop a single (user question + assistant reply) turn by the assistant message's index. Used by Retry
     * (item #9) to remove the failed turn before re-sending the same question, so the history stays clean.
     */
    dropTurn: (state, action: PayloadAction<{ index: number }>) => {
      const i = action.payload.index
      const path = pathToRoot(state.conversationNodes as NodeMap, state.currentNodeId)
      if (i < 0 || i >= path.length) return
      const assistantNode = path[i]
      // Also remove the paired preceding user question, if present — it is this node's ancestor on the path.
      const userNode = i > 0 && path[i - 1].message.role === 'user' ? path[i - 1] : undefined
      const removeRoot = userNode ?? assistantNode
      // The earliest removed node's parent becomes the new tip (Retry re-sends a fresh child under it).
      state.currentNodeId = removeRoot.parentId
      removeSubtree(state, removeRoot.id)
      if (state.rootNodeId && !state.conversationNodes[state.rootNodeId]) state.rootNodeId = state.currentNodeId
      syncActivePath(state)
    },
    /**
     * Move the conversation's "you are here" to `nodeId` (W-1). Two uses: "branch here" (point at an earlier
     * node so the NEXT turn appends a sibling branch there) and the branch switcher (jump onto another branch's
     * tip). No-op for an unknown id. Purely navigational — never mutates the tree, so branches are preserved.
     */
    setCurrentNode: (state, action: PayloadAction<{ nodeId: string }>) => {
      if (!state.conversationNodes[action.payload.nodeId]) return
      state.currentNodeId = action.payload.nodeId
      syncActivePath(state)
    },
    /**
     * Add a workspace-global fact (W-1). Facts bridge conversation branches: they are sent to the agent every
     * turn (appended to `vizContext`) regardless of which branch the active path is on. Trimmed; blank and
     * exact-duplicate facts are ignored. Persisted with the workspace.
     */
    addGlobalContext: (state, action: PayloadAction<{ text: string }>) => {
      const text = action.payload.text.trim()
      if (text === '' || state.globalContext.includes(text)) return
      state.globalContext.push(text)
    },
    /** Remove a workspace-global fact by index (W-1). */
    removeGlobalContext: (state, action: PayloadAction<{ index: number }>) => {
      const i = action.payload.index
      if (i >= 0 && i < state.globalContext.length) state.globalContext.splice(i, 1)
    },
    /**
     * Toggle a workspace item's pinned state (W-2). `id` is a derived workspace-item id (chart:`<directiveKey>`
     * / `report` / note:`<noteId>`). Pinned items surface at the top of the unified panel. Persisted with the
     * workspace. NB: the "agent cannot modify a pinned item" enforcement is a later follow-up (W-2b) — this
     * reducer only records the user's intent.
     */
    togglePinItem: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload
      const idx = state.pinnedItemIds.indexOf(id)
      if (idx >= 0) state.pinnedItemIds.splice(idx, 1)
      else state.pinnedItemIds.push(id)
    },
    /** Add a manual NOTE item (W-2). Mints a stable id; optional starting text/title/subtitle (default blank). */
    addNote: (
      state,
      action: PayloadAction<{ text?: string; title?: string; subtitle?: string } | undefined>
    ) => {
      const p = action.payload ?? {}
      state.notes.push({ id: uuidv4(), title: p.title, subtitle: p.subtitle, text: p.text ?? '' })
    },
    /** Patch a NOTE's text/title/subtitle (W-2). Only the provided fields change; unknown id is a no-op. */
    updateNote: (
      state,
      action: PayloadAction<{ id: string; text?: string; title?: string; subtitle?: string }>
    ) => {
      const note = state.notes.find((n) => n.id === action.payload.id)
      if (!note) return
      if (action.payload.text !== undefined) note.text = action.payload.text
      if (action.payload.title !== undefined) note.title = action.payload.title
      if (action.payload.subtitle !== undefined) note.subtitle = action.payload.subtitle
    },
    /** Remove a NOTE item (W-2). Also drops its pin + any card-meta so no ghost pin/meta is left behind. */
    removeNote: (state, action: PayloadAction<{ id: string }>) => {
      state.notes = state.notes.filter((n) => n.id !== action.payload.id)
      const itemId = `note:${action.payload.id}`
      state.pinnedItemIds = state.pinnedItemIds.filter((p) => p !== itemId)
      delete state.itemMeta[itemId]
    },
    /**
     * Set a DERIVED chart/report item's editable card title/subtitle (W-2). Stored as an override in `itemMeta`
     * keyed by the workspace-item id (notes carry their own meta and use `updateNote` instead). Only provided
     * fields change. Setting a field to '' clears it (falls back to the derived default in the panel).
     */
    setItemMeta: (
      state,
      action: PayloadAction<{ id: string; title?: string; subtitle?: string; note?: string }>
    ) => {
      const { id, title, subtitle, note } = action.payload
      const meta = state.itemMeta[id] ?? {}
      if (title !== undefined) meta.title = title === '' ? undefined : title
      if (subtitle !== undefined) meta.subtitle = subtitle === '' ? undefined : subtitle
      if (note !== undefined) meta.note = note === '' ? undefined : note
      state.itemMeta[id] = meta
    },
    /**
     * Persist (or clear) a chart card's PINNED view — the full shared-state snapshot captured when the user
     * pins it. Rides in `itemMeta` so it survives reload (the VisualizationSlice source that renders it is
     * in-session only); on reload ChatCubeChart seeds the recreated source from it. `view` omitted → cleared
     * (on unpin).
     */
    setItemPinnedView: (state, action: PayloadAction<{ id: string; view?: SourceSharedState }>) => {
      const { id, view } = action.payload
      const meta = state.itemMeta[id] ?? {}
      meta.pinnedView = view
      state.itemMeta[id] = meta
    },
    /** Clear the conversation, e.g. when the user navigates to a different order/project. */
    resetConversation: (state, action: PayloadAction<{ scopeKey: string }>) => {
      state.messages = []
      state.conversationNodes = {}
      state.currentNodeId = null
      state.rootNodeId = null
      state.openRequests = []
      state.scopeKey = action.payload.scopeKey
      // Fresh session → fresh conversation id, so the new conversation never reads the old one's cached tool results.
      state.conversationId = uuidv4()
      state.kronaSelections = {}
      state.openGraphs = []
      state.focusedGraphKey = undefined
      state.comparisonCaveat = undefined
      state.lastFailedQuestion = undefined
      state.composerSeed = undefined
      state.currentLens = undefined
      state.currentReport = emptyReport()
      state.reportRevision = 0
      state.globalContext = []
      state.notes = []
      state.pinnedItemIds = []
      state.itemMeta = {}
    },
    /**
     * Resume a saved workspace (W-0). Replaces the conversation + view from a `hydrateFromWorkspace` payload:
     * sets messages / openGraphs / currentReport (blocks+title, html=null, status='idle') / kronaSelections /
     * globalContext / scopeKey, and clears in-flight + volatile state so a resumed session starts clean. The
     * report re-renders afterwards: the page dispatches `applyReportEdits([{op:'REPLACE', ...}])` off the
     * hydrated blocks so `RenderReportEpic` repopulates the ReportPanel (see ChatPage). `reportRevision` is
     * bumped so the Report tab notices the change.
     */
    hydrateWorkspace: (state, action: PayloadAction<Partial<IChatState>>) => {
      const p = action.payload
      // W-1: restore the TREE (nodes + current + root); `messages` is rebuilt from it below, never taken raw.
      if (p.conversationNodes) state.conversationNodes = p.conversationNodes
      if (p.currentNodeId !== undefined) state.currentNodeId = p.currentNodeId
      if (p.rootNodeId !== undefined) state.rootNodeId = p.rootNodeId
      if (p.openGraphs) state.openGraphs = p.openGraphs
      if (p.kronaSelections) state.kronaSelections = p.kronaSelections
      if (p.globalContext) state.globalContext = p.globalContext
      // W-2: restore the unified-panel state (notes + pins + card-meta). Missing on a resumed v2 blob → left empty.
      if (p.notes) state.notes = p.notes
      if (p.pinnedItemIds) state.pinnedItemIds = p.pinnedItemIds
      if (p.itemMeta) state.itemMeta = p.itemMeta
      if (p.currentReport) state.currentReport = p.currentReport
      if (p.scopeKey !== undefined) state.scopeKey = p.scopeKey
      // Resuming replaces the current conversation — drop any in-flight/volatile state from before.
      // A resumed session is a fresh conversation w.r.t. the server tool-call cache, so re-mint its id.
      state.conversationId = uuidv4()
      state.openRequests = []
      state.focusedGraphKey = undefined
      state.comparisonCaveat = undefined
      state.lastFailedQuestion = undefined
      state.currentLens = undefined
      state.reportRevision += 1
      syncActivePath(state)
    },
    /** The user zoomed a Krona sunburst (reported from the embed iframe). Empty path = back at the root. */
    kronaNodeSelected: (state, action: PayloadAction<{ contextId: string; path: string[] }>) => {
      state.kronaSelections[action.payload.contextId] = action.payload.path
    },
    /** Highlight/scroll to an open graph (from a reply chip or user click). */
    focusGraph: (state, action: PayloadAction<{ key: string }>) => {
      state.focusedGraphKey = action.payload.key
    },
    /** Remove a graph from the canvas. Also drops its W-2 pin + card-meta so no ghost item is left behind. */
    closeGraph: (state, action: PayloadAction<{ key: string }>) => {
      state.openGraphs = state.openGraphs.filter((g) => directiveKey(g) !== action.payload.key)
      if (state.focusedGraphKey === action.payload.key) state.focusedGraphKey = undefined
      const itemId = `chart:${action.payload.key}`
      state.pinnedItemIds = state.pinnedItemIds.filter((p) => p !== itemId)
      delete state.itemMeta[itemId]
    },
    /** Flip the "Cite sources" preference (Epic A #7 Tier 2). Deliberately NOT reset by resetConversation. */
    setCiteSources: (state, action: PayloadAction<boolean>) => {
      state.citeSources = action.payload
    },
    /**
     * "Ask about this" (fast-follow): seed the composer with a context-aware starter question about a chart.
     * The composer effect sets the draft + focuses; it does NOT auto-send (the user reviews/edits then sends).
     * The nonce bump makes the effect re-fire even when the same text is seeded again.
     */
    seedComposer: (state, action: PayloadAction<{ text: string }>) => {
      state.composerSeed = action.payload.text
      state.composerSeedNonce += 1
    },
    /**
     * Apply a turn's report directives to `currentReport.blocks` (IR-1). Mirrors `applyCanvasPayload` for the
     * graph canvas: fold each edit in order, keeping block ids stable so UPDATE/REMOVE/REORDER can target them.
     * A REPLACE/ADD block missing an `id` gets one assigned; UPDATE preserves the target's id. Bumps
     * `reportRevision` so the ReportPanel tab can show an "updated" indicator. The actual server render happens
     * afterwards in the epic (one render per turn, off the resulting blocks).
     */
    applyReportEdits: (state, action: PayloadAction<ReportEdit[]>) => {
      const report = state.currentReport
      action.payload.forEach((edit) => {
        switch (edit.op) {
          case 'REPLACE':
            report.blocks = (edit.blocks ?? []).map(withId)
            report.title = edit.title
            break
          case 'ADD': {
            if (!edit.block) break
            const block = withId(edit.block)
            const idx = edit.afterId ? report.blocks.findIndex((b) => b.id === edit.afterId) : -1
            if (idx >= 0) report.blocks.splice(idx + 1, 0, block)
            else report.blocks.push(block)
            break
          }
          case 'UPDATE': {
            if (!edit.block || !edit.targetId) break
            const idx = report.blocks.findIndex((b) => b.id === edit.targetId)
            // Replace the block wholesale but PRESERVE its id so future edits keep targeting it.
            if (idx >= 0) report.blocks[idx] = { ...edit.block, id: edit.targetId }
            break
          }
          case 'REMOVE':
            if (edit.targetId) report.blocks = report.blocks.filter((b) => b.id !== edit.targetId)
            break
          case 'REORDER': {
            if (!edit.order) break
            const byId = new Map(report.blocks.map((b) => [b.id, b]))
            const named = new Set(edit.order)
            const reordered = edit.order
              .map((id) => byId.get(id))
              .filter((b): b is ReportBlock => b !== undefined)
            // Blocks not named in `order` keep their relative position, appended after the reordered ones.
            const rest = report.blocks.filter((b) => b.id === undefined || !named.has(b.id))
            report.blocks = [...reordered, ...rest]
            break
          }
          default:
            break
        }
      })
      state.reportRevision += 1
    },
    /**
     * Record the outcome of a report render (IR-1). `status` moves idle → rendering → idle|error; `html` is
     * the server-rendered document (omitted while `rendering` so the prior render stays on screen).
     */
    setReportRender: (state, action: PayloadAction<{ html?: string | null; status: ICurrentReport['status'] }>) => {
      state.currentReport.status = action.payload.status
      if (action.payload.html !== undefined) state.currentReport.html = action.payload.html
    },
    /** Discard the report document (blocks + render). */
    clearReport: (state) => {
      state.currentReport = emptyReport()
      state.reportRevision += 1
    }
  }
})

/** Whether the user asked the assistant to cite evidence inline this session. */
export const selectCiteSources = (state: { chat: IChatState }): boolean => state.chat.citeSources

/** The stable per-session conversation id sent as `ChatRequest.conversationId` (keys the server tool-call cache). */
export const selectConversationId = (state: { chat: IChatState }): string => state.chat.conversationId

/** The "current lens" the most recent query resolved to (blast source + SPIN/group knobs), or undefined. */
export const selectCurrentLens = (state: { chat: IChatState }): VizProvenance | undefined => state.chat.currentLens

/** The agent-authored report document (IR-1) — blocks + the last server render. */
export const selectCurrentReport = (state: { chat: IChatState }): ICurrentReport => state.chat.currentReport

/**
 * The active conversation PATH (root→current) as linear messages (W-1). Backed by the maintained `messages`
 * cache, so it is referentially stable across renders and equal to what the UI has always rendered.
 */
export const selectActivePath = (state: { chat: IChatState }): IChatMessage[] => state.chat.messages

/** The active path as TREE nodes (root→current) — the UI derives per-message branch controls from these. */
export const selectActivePathNodes = (state: { chat: IChatState }): ChatNode[] =>
  pathToRoot(state.chat.conversationNodes, state.chat.currentNodeId)

/** Workspace-global facts bridging branches (W-1) — sent to the agent each turn via `vizContext`. */
export const selectGlobalContext = (state: { chat: IChatState }): string[] => state.chat.globalContext

/** Manual NOTE items in the unified Workspace panel (W-2). */
export const selectNotes = (state: { chat: IChatState }): WorkspaceNote[] => state.chat.notes

/** Pinned workspace-item ids (W-2) — chart:`<directiveKey>` / `report` / note:`<noteId>`. */
export const selectPinnedItemIds = (state: { chat: IChatState }): string[] => state.chat.pinnedItemIds

/** Editable card-meta overrides for the derived chart/report items (W-2), keyed by workspace-item id. */
export const selectItemMeta = (state: { chat: IChatState }): Record<string, WorkspaceItemMeta> =>
  state.chat.itemMeta

export type { ChatNode } from './conversationTree.js'

export default chatSlice.reducer
